import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import {
  type EventTransformerHooks,
  useEventTracking,
  type UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import type { CarsState } from "../store";
import {
  type AppliedCarsFilter,
  CAR_PRODUCT_TYPE,
  type CarEntry,
  CarEventName,
  type CarHTSEvent,
  type CarViewedCalendar,
  type ClickedCarOnCarsList,
  GroundShopTrackingEvents,
  type ViewedCarDetails,
  type ViewedCarDetailsLoading,
  type ViewedCarSearch,
  type ViewedCarsList,
  type ViewedCarsListLoading,
} from "./events";
import {
  getAppliedCarsFilterEncryptedTrackingProperties,
  getAppliedCarsFilterTrackingProperties,
  getCarEntryEncryptedTrackingProperties,
  getCarEntryTrackingProperties,
  getViewedCarDetailsEncryptedTrackingProperties,
  getViewedCarDetailsTrackingProperties,
  getViewedCarListEncryptedTrackingProperties,
  getViewedCarListTrackingProperties,
} from "./selectors";
import {
  CarsRawEventName,
  type CarsSearchLoadedRawEvent,
  type CarsAppLoadedRawEvent,
  type CarsRawEvent,
  type CarsAvailabilityPageLoadedRawEvent,
  type CarsAppliedFilterRawEvent,
  type CarsEntryTrackingProperties,
  type ViewedCarsListTrackingProperties,
  type CarsShopLoadedRawEvent,
  type CarViewedCalendarRawEvent,
  type ViewedCarShopPageLoadingRawEvent,
  type ViewedCarAvailabilityPageLoadingRawEvent,
  type ClickedCarOnAvailabilityRawEvent,
} from "./types";

export const commonProperties = {
  productType: CAR_PRODUCT_TYPE,
};

export const useCarSearchLoadedTransformer: UseInstantTransformEvent<
  CarsSearchLoadedRawEvent,
  ViewedCarSearch
> = () => {
  return useCallback(() => {
    const name = CarEventName.viewed_cars_search;
    const htsEvent: ViewedCarSearch = {
      name,
      properties: {},
    };

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, []);
};

export const useCarViewedCalendarTransformer: UseInstantTransformEvent<
  CarViewedCalendarRawEvent,
  CarViewedCalendar
> = () => {
  return useCallback(() => {
    const name = CarEventName.car_viewed_calendar;
    const htsEvent: CarViewedCalendar = {
      name,
      properties: {},
    };
    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, []);
};

export const useCarAppLoadedTransformer: UseInstantTransformEvent<
  CarsAppLoadedRawEvent,
  CarEntry
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultCarsEntryEventTransformer<CarEntry>(
      CarEventName.cars_entry,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const defaultCarsEntryEventTransformer = <T extends CarHTSEvent>(
  eventName: T["name"],
  providers: Providers,
  extraProperties: Omit<
    T["properties"],
    | keyof CarsEntryTrackingProperties["properties"]
    | keyof typeof commonProperties
  >
): {
  name: T["name"];
  properties: T["properties"];
  encryptedProperties: string[];
} => {
  const { properties } = getCarEntryTrackingProperties(providers.reduxState);
  const encryptedProperties = getCarEntryEncryptedTrackingProperties(
    providers.reduxState
  );
  return {
    name: eventName,
    properties: {
      ...properties,
      ...extraProperties,
      ...commonProperties,
    },
    encryptedProperties,
  };
};

export const useCarAvailabilityPageLoadedTransformer: UseInstantTransformEvent<
  CarsAvailabilityPageLoadedRawEvent,
  ViewedCarsList
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultCarsViewedListEventTransformer<ViewedCarsList>(
      CarEventName.viewed_cars_list,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const defaultCarsViewedListEventTransformer = <T extends CarHTSEvent>(
  eventName: T["name"],
  providers: Providers,
  extraProperties: Omit<
    T["properties"],
    | keyof ViewedCarsListTrackingProperties["properties"]
    | keyof typeof commonProperties
  >
): {
  name: T["name"];
  properties: T["properties"];
  encryptedProperties: string[];
} => {
  const { properties } = getViewedCarListTrackingProperties(
    providers.reduxState
  );
  const encryptedProperties = getViewedCarListEncryptedTrackingProperties(
    providers.reduxState
  );
  return {
    name: eventName,
    properties: {
      ...properties,
      ...extraProperties,
      ...commonProperties,
    },
    encryptedProperties,
  };
};

export const useCarsAppliedFilterTransformer: UseInstantTransformEvent<
  CarsAppliedFilterRawEvent,
  AppliedCarsFilter
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent =
      defaultAppliedCarsFilterEventTransformer<AppliedCarsFilter>(
        CarEventName.applied_cars_filter,
        getProviders()
      );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const defaultAppliedCarsFilterEventTransformer = <T extends CarHTSEvent>(
  eventName: T["name"],
  providers: Providers
): {
  name: T["name"];
  properties: T["properties"];
  encryptedProperties: string[];
} => {
  const { properties } = getAppliedCarsFilterTrackingProperties(
    providers.reduxState
  );
  const encryptedProperties = getAppliedCarsFilterEncryptedTrackingProperties(
    providers.reduxState
  );
  return {
    name: eventName,
    properties: {
      ...properties,
      ...commonProperties,
    },
    encryptedProperties,
  };
};

export const useCarShopLoadedTransformer: UseInstantTransformEvent<
  CarsShopLoadedRawEvent,
  ViewedCarDetails
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultViewedCarDetailsEventTransformer<ViewedCarDetails>(
      CarEventName.viewed_cars_details,
      getProviders()
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const useViewedCarAvailabilityLoadingTransformer: UseInstantTransformEvent<
  ViewedCarAvailabilityPageLoadingRawEvent,
  ViewedCarsListLoading
> = () => {
  return useCallback(
    (event) => ({
      name: GroundShopTrackingEvents.viewed_car_availability_loading,
      properties: {
        ...commonProperties,
        ...event.properties,
      },
    }),
    []
  );
};

export const useViewedCarShopLoadingTransformer: UseInstantTransformEvent<
  ViewedCarShopPageLoadingRawEvent,
  ViewedCarDetailsLoading
> = () => {
  return useCallback(
    (event) => ({
      name: GroundShopTrackingEvents.viewed_car_shop_loading,
      properties: {
        ...commonProperties,
        ...event.properties,
      },
    }),
    []
  );
};

export const useClickedCarOnAvailabilityTransformer: UseInstantTransformEvent<
  ClickedCarOnAvailabilityRawEvent,
  ClickedCarOnCarsList
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent =
      defaultCarsViewedListEventTransformer<ClickedCarOnCarsList>(
        CarEventName.selected_car_from_list,
        getProviders(),
        {}
      );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const defaultViewedCarDetailsEventTransformer = <T extends CarHTSEvent>(
  eventName: T["name"],
  providers: Providers
): {
  name: T["name"];
  properties: T["properties"];
  encryptedProperties: string[];
} => {
  const { properties } = getViewedCarDetailsTrackingProperties(
    providers.reduxState
  );
  const encryptedProperties = getViewedCarDetailsEncryptedTrackingProperties(
    providers.reduxState
  );
  return {
    name: eventName,
    properties: {
      ...properties,
      ...commonProperties,
    },
    encryptedProperties,
  };
};

export const CARS_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  CarsRawEvent,
  CarHTSEvent
> = {
  [CarsRawEventName.CarSearchLoaded]: [useCarSearchLoadedTransformer],
  [CarsRawEventName.CarViewedCalendar]: [useCarViewedCalendarTransformer],
  [CarsRawEventName.CarAppLoaded]: [useCarAppLoadedTransformer],
  [CarsRawEventName.CarAvailabilityPageLoaded]: [
    useCarAvailabilityPageLoadedTransformer,
  ],
  [CarsRawEventName.CarsAppliedFilter]: [useCarsAppliedFilterTransformer],
  [CarsRawEventName.CarShopLoaded]: [useCarShopLoadedTransformer],
  [CarsRawEventName.ViewedCarAvailabilityPageLoading]: [
    useViewedCarAvailabilityLoadingTransformer,
  ],
  [CarsRawEventName.ViewedCarShopPageLoading]: [
    useViewedCarShopLoadingTransformer,
  ],
  [CarsRawEventName.ClickedCarOnAvailability]: [
    useClickedCarOnAvailabilityTransformer,
  ],
};

function useGetProviders(): () => Providers {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(
    () => ({
      trackingAttributes: getAllAttributes(),
      reduxState: reduxContext.store.getState(),
    }),
    [reduxContext.store, getAllAttributes]
  );
}

export const trackOnlyOncePerSession = (event: CarHTSEvent): boolean => {
  const sessionStorageKey = `car_event_${event.name}`;
  if (sessionStorage.getItem(sessionStorageKey) ?? "") {
    return false;
  }
  sessionStorage.setItem(sessionStorageKey, "true");
  return true;
};

export const shouldTrackEvent = (event: CarHTSEvent): boolean => {
  if (event.name === CarEventName.cars_entry) {
    return trackOnlyOncePerSession(event);
  }
  return true;
};

export interface Providers {
  reduxState: CarsState;
}
